/* =====================================================================
 * SkillMarketplacePage — "마켓플레이스 › 에이전트 스킬" 전체 페이지
 *
 * 채팅 입력창의 스킬 관리 모달(`chat/SkillModal.tsx`)을 페이지로 펼친
 * 것이다. 탭 셋(내 스킬 · 레퍼런스 스킬 · 공유 스킬)은 모달의 탭
 * 컴포넌트를 그대로 재사용하되, 내 스킬 탭에는 사내 공유 토글을 켜고
 * 세 탭 모두 "적용하기" 버튼은 끈다(적용은 채팅 모달에서만) 그리고 카드
 * 목록을 페이지로 나눈다.
 *
 * frameless(showTopBar:false)로 마운트되므로 헤더와 사이드바 펼치기
 * 버튼을 페이지가 직접 그린다 — `MemoryPage` / `DashboardPage` 와 같은
 * PageScroll › PageInner 골격. 카드 영역(Surface)은 모달의 TabContent 처럼
 * 높이를 고정해 탭 안의 ScrollArea 가 스크롤하고(카테고리 칩·검색창이
 * 위에 고정된다), 뷰포트가 그보다 낮으면 페이지가 스크롤한다. 테두리는
 * 두지 않는다 — 페이지 배경 위에 탭 바의 밑줄만 구분선이다.
 * ===================================================================== */

import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { BookOpen, FolderOpen, PanelLeft, Users } from 'lucide-react';
import { type TabItem } from '@auw/workspace-ui';
import { useChatStore, useSkillStore } from '@auw/stores';
import { registerWorkspaceNavigationGuard } from '@auw/workspace-features';
import { workspaceButtonStyles } from '../common/workspaceSurface';
import {
  PageHeader,
  PageHeaderText,
  PageInner,
  PageScroll,
  PageSubtitle,
  PageTitle,
} from '../dashboard/dashboard.styles';
import { MySkillsTab, ReferenceTab } from '../chat/SkillModal';
import { SharedSkillsTab } from '../chat/SharedSkillsTab';
import SkillSourcePanel, { type SkillSourceTarget } from '../chat/SkillSourcePanel';
import { CardGrid, SkillTabs } from '../chat/skillCards';

type MarketplaceTab = 'my' | 'ref' | 'shared';
const MARKETPLACE_TABS: readonly MarketplaceTab[] = ['my', 'ref', 'shared'];
function isMarketplaceTab(value: string | null): value is MarketplaceTab {
  return MARKETPLACE_TABS.includes(value as MarketplaceTab);
}

/* 탭 컴포넌트는 모달을 닫으려고 `onClose` 를 부르지만 페이지에는 닫을 것이
 * 없고, 적용 버튼 자체를 끄므로(showApply=false) 실제로 불리지도 않는다. */
const noop = () => {};

/* 3열 그리드 기준 한 페이지. 내 스킬 탭은 첫 칸이 업로드 카드라 8장 + 1. */
const MY_SKILLS_PAGE_SIZE = 8;
const LIST_PAGE_SIZE = 9;

export const SkillMarketplacePage: React.FC = () => {
  const { t } = useTranslation('common');
  const [searchParams] = useSearchParams();
  const mainSidebarOpen = useChatStore((s) => s.sidebarOpen);
  const setMainSidebarOpen = useChatStore((s) => s.setSidebarOpen);
  const {
    skills, activeSkillIds, isLoading, error,
    fetchSkills, uploadSkills, deleteSkill, toggleSkill,
  } = useSkillStore();

  /* `?tab=shared` 딥링크 — 사이드바 진입은 기본 '내 스킬'. */
  const requestedTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<MarketplaceTab>(
    isMarketplaceTab(requestedTab) ? requestedTab : 'my',
  );
  const [sourceTarget, setSourceTarget] = useState<SkillSourceTarget | null>(null);
  const [sourceState, setSourceState] = useState({ dirty: false, saving: false });

  useEffect(() => {
    void fetchSkills();
  }, [fetchSkills]);

  const canLeaveSource = () =>
    !sourceState.saving && (!sourceState.dirty || window.confirm(t('skill.source.discard')));
  /* 모달과 달리 페이지는 사이드바가 살아 있다 — 소스 편집 중에 다른 모드로
   * 나가는 앱 내 이동(openWorkspaceMode 등)도 같은 확인을 거치도록 가드를
   * 등록한다. 최신 클로저를 ref 로 넘겨 등록은 소스가 열려 있는 동안 한 번만. */
  const leaveRef = useRef(canLeaveSource);
  leaveRef.current = canLeaveSource;
  useEffect(() => {
    if (!sourceTarget) return;
    return registerWorkspaceNavigationGuard(() => leaveRef.current());
  }, [sourceTarget]);
  const resetSource = () => {
    setSourceTarget(null);
    setSourceState({ dirty: false, saving: false });
  };
  const closeSource = () => { if (canLeaveSource()) resetSource(); };
  const changeTab = (id: string) => {
    if (!isMarketplaceTab(id)) return;
    if (sourceTarget && !canLeaveSource()) return;
    resetSource();
    setActiveTab(id);
  };

  const tabs: TabItem[] = [
    { id: 'my', label: t('skill.tabMy'), icon: <FolderOpen size={14} /> },
    { id: 'ref', label: t('skill.tabReference'), icon: <BookOpen size={14} /> },
    { id: 'shared', label: t('skill.tabShared'), icon: <Users size={14} /> },
  ];

  return (
    <Page>
      <PageScroll>
        <PageInner>
          {/* 메인 사이드바를 접은 뒤 다시 펼치는 유일한 버튼 (MemoryPage 와 동일). */}
          {!mainSidebarOpen && (
            <Toolbar>
              <ToolbarIconButton
                type="button"
                onClick={() => setMainSidebarOpen(true)}
                title={t('workspace.expandMainPanel')}
                aria-label={t('workspace.expandMainPanel')}
              >
                <PanelLeft size={16} />
              </ToolbarIconButton>
            </Toolbar>
          )}

          <PageHeader>
            <PageHeaderText>
              <PageTitle>{t('skill.marketplace.title')}</PageTitle>
              <PageSubtitle>{t('skill.marketplace.subtitle')}</PageSubtitle>
            </PageHeaderText>
          </PageHeader>

          <Surface>
            {!sourceTarget && <SkillTabs items={tabs} activeId={activeTab} onChange={changeTab} />}
            {sourceTarget ? (
              <SkillSourcePanel
                key={`${sourceTarget.kind}:${sourceTarget.id}`}
                target={sourceTarget}
                onBack={closeSource}
                onStateChange={setSourceState}
                onSaved={() => { void fetchSkills(); }}
              />
            ) : (
              <>
                {activeTab === 'my' && (
                  <MySkillsTab
                    onOpenSource={setSourceTarget}
                    skills={skills}
                    activeSkillIds={activeSkillIds}
                    isLoading={isLoading}
                    error={error}
                    onUpload={uploadSkills}
                    onDelete={deleteSkill}
                    onToggle={toggleSkill}
                    onClose={noop}
                    enableSharing
                    showApply={false}
                    pageSize={MY_SKILLS_PAGE_SIZE}
                  />
                )}
                {activeTab === 'ref' && (
                  <ReferenceTab
                    onOpenSource={setSourceTarget}
                    activeSkillIds={activeSkillIds}
                    onToggle={toggleSkill}
                    onClose={noop}
                    showApply={false}
                    pageSize={LIST_PAGE_SIZE}
                  />
                )}
                {activeTab === 'shared' && (
                  <SharedSkillsTab
                    onOpenSource={setSourceTarget}
                    activeSkillIds={activeSkillIds}
                    onToggle={toggleSkill}
                    showApply={false}
                    pageSize={LIST_PAGE_SIZE}
                  />
                )}
              </>
            )}
          </Surface>
        </PageInner>
      </PageScroll>
    </Page>
  );
};

export default SkillMarketplacePage;

/* ------------------------------------------------------------------ */
/* Styles — 색·간격·라운드는 모두 테마 토큰 경유                          */
/* ------------------------------------------------------------------ */

const Page = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: ${({ theme }) => theme.colors.bgPrimary};
`;

/** 사이드바가 접혔을 때의 펼치기 버튼 자리. 그 외에는 렌더하지 않는다. */
const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 28px;
`;

const ToolbarIconButton = styled.button`
  width: 28px;
  height: 28px;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textMuted};
  flex-shrink: 0;

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
  ${workspaceButtonStyles}
`;

/* 모달의 TabContent 에 해당한다. 높이를 고정해야 탭 안의 ScrollArea 가
 * 스크롤한다 — 헤더·여백(약 220px)을 뺀 나머지를 쓰고, 낮은 뷰포트에서는
 * min-height 로 버티며 바깥 PageScroll 이 대신 스크롤한다. 테두리 없이
 * 페이지 배경 위에 바로 놓인다. */
const Surface = styled.section`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 220px);
  min-height: 480px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    height: calc(100vh - 200px);
    min-height: 420px;
  }

  /* 폰 폭에서는 카드 한 장에 푸터 버튼 넷(적용·수정·삭제·공유)이 들어가야
   * 하므로 모달의 2열 대신 1열로 편다. 모달 자체는 그대로. */
  @media (max-width: 480px) {
    ${CardGrid} { grid-template-columns: 1fr; }
  }
`;
